import { useEffect, useRef, useState } from "react";

/** Soft keyboards are taller than the Safari URL-bar collapse (~60–100px). */
const KEYBOARD_HEIGHT_THRESHOLD_PX = 150;
/** Pinch-zoom also shrinks `visualViewport.height`. Ignore that. */
const MAX_SCALE = 1.05;
/** Field-to-field focus moves can report a full viewport for one frame. */
const HIDE_RELEASE_DELAY_MS = 250;

const TYPING_TAGS = new Set(["INPUT", "TEXTAREA", "IFRAME"]);

export interface VirtualKeyboardSnapshot {
  activeElementTag: string | null;
  contentEditable: boolean;
  /**
   * Tallest `visualViewport.height` seen while no text entry was focused.
   * `interactive-widget=resizes-content` shrinks `innerHeight` with the
   * keyboard, so that gap cannot tell the keyboard from the page.
   */
  restingVisualViewportHeight: number | null;
  visualViewportHeight: number | null;
  visualViewportScale: number | null;
}

/**
 * True only when a text entry is focused AND the visual viewport shrank by
 * more than a soft keyboard from its resting height. Desktop focus does not
 * shrink the viewport, so it stays false there.
 */
export function isVirtualKeyboardOpen(snapshot: VirtualKeyboardSnapshot): boolean {
  const { visualViewportHeight, visualViewportScale, restingVisualViewportHeight } = snapshot;
  if (
    visualViewportHeight == null ||
    visualViewportScale == null ||
    restingVisualViewportHeight == null
  ) {
    return false;
  }

  const typing =
    snapshot.contentEditable ||
    (snapshot.activeElementTag != null && TYPING_TAGS.has(snapshot.activeElementTag));
  if (!typing) return false;
  if (visualViewportScale >= MAX_SCALE) return false;

  return restingVisualViewportHeight - visualViewportHeight > KEYBOARD_HEIGHT_THRESHOLD_PX;
}

function isTyping(el: HTMLElement | null): boolean {
  if (!el) return false;
  return el.isContentEditable || TYPING_TAGS.has(el.tagName);
}

export function useVirtualKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    let releaseTimer: ReturnType<typeof setTimeout> | undefined;
    // Seed before the keyboard opens. A focused field on the first frame
    // still has the full viewport; iOS shrinks it on a later resize.
    let restingHeight = viewport.height;

    const compute = () => {
      const el = document.activeElement as HTMLElement | null;
      if (!isTyping(el)) {
        restingHeight = Math.max(restingHeight, viewport.height);
      }
      const next = isVirtualKeyboardOpen({
        activeElementTag: el?.tagName ?? null,
        contentEditable: Boolean(el?.isContentEditable),
        restingVisualViewportHeight: restingHeight,
        visualViewportHeight: viewport.height,
        visualViewportScale: viewport.scale,
      });
      if (next) {
        if (releaseTimer) {
          clearTimeout(releaseTimer);
          releaseTimer = undefined;
        }
        if (!openRef.current) {
          openRef.current = true;
          setOpen(true);
        }
        return;
      }
      if (!openRef.current || releaseTimer) return;
      releaseTimer = setTimeout(() => {
        releaseTimer = undefined;
        openRef.current = false;
        setOpen(false);
      }, HIDE_RELEASE_DELAY_MS);
    };

    viewport.addEventListener("resize", compute);
    window.addEventListener("focusin", compute);
    window.addEventListener("focusout", compute);

    return () => {
      if (releaseTimer) clearTimeout(releaseTimer);
      viewport.removeEventListener("resize", compute);
      window.removeEventListener("focusin", compute);
      window.removeEventListener("focusout", compute);
    };
  }, []);

  return open;
}
